import { useOutletContext } from 'react-router-dom';
import type { BrandColor, SidebarSkin, ThemeMode } from '../lib/types/membership.js';

/**
 * Passed by `AuthenticatedShell` to every routed page via `<Outlet
 * context={...} />` — the live appearance-preview hooks `FirstLoginProfileFlow`
 * and `AccountPage` use (see `AuthenticatedShell.tsx`'s own doc comment on
 * `appearancePreview`), so any settings page under this shell can repaint
 * the real sidebar/topbar/theme the instant a color/skin/mode is picked,
 * not just after a save + refetch.
 */
export interface ShellOutletContext {
  readonly onAppearancePreview: (appearance: {
    readonly brandColor: BrandColor;
    readonly sidebarSkin: SidebarSkin;
  }) => void;
  /** Applies a picked theme mode to the real, global `ThemeProvider`
   * immediately (`'system'` resolved once, at pick time, via
   * `prefers-color-scheme`) — see `AuthenticatedShell.tsx`'s own doc
   * comment on why this is the same `setMode` the topbar's `ThemeToggle`
   * uses, not a separate mechanism. */
  readonly onThemeModePreview: (mode: ThemeMode) => void;
}

export function useShellOutletContext(): ShellOutletContext {
  return useOutletContext<ShellOutletContext>();
}
